import type { BulkPackSubmissionActionBody, PackSubmissionAction } from '@planuze/admin-contracts';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  approvePackSubmission,
  bulkPackSubmissionAction,
  comparePackVersions,
  getPackSubmission,
  listPackSubmissions,
  type PackSubmissionsQuery,
  rejectPackSubmission,
  reopenPackSubmission,
  requestChangesPackSubmission,
  rescanPackSubmission,
  startReviewPackSubmission,
} from '../lib/pack-submissions-api.js';

/* Hooks TanStack Query da fila de moderação de packs (TASK-29-14). */

export function usePackSubmissionsList(filters: PackSubmissionsQuery) {
  return useInfiniteQuery({
    queryKey: ['pack-submissions', 'list', filters],
    queryFn: ({ pageParam }) => listPackSubmissions({ ...filters, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

/** Detalhe da submissão p/ a tela do backoffice (PUB-11 / ADR-0462). */
export function usePackSubmissionDetail(id: string) {
  return useQuery({
    queryKey: ['pack-submissions', 'detail', id],
    queryFn: () => getPackSubmission(id),
  });
}

/** Diff de manifest entre 2 versões publicadas (ADR-0473). Só dispara com `a` e `b` (a !== b). */
export function useCompareSubmissionVersions(id: string, a: string | null, b: string | null) {
  return useQuery({
    queryKey: ['pack-submissions', 'compare', id, a, b],
    queryFn: () => comparePackVersions(id, a ?? '', b ?? ''),
    enabled: a !== null && b !== null && a !== b,
  });
}

export type SubmissionAction = PackSubmissionAction;

interface ActionInput {
  readonly id: string;
  readonly action: SubmissionAction;
  readonly reason: string;
}

/* Uma mutation para as ações. `start-review` não tem motivo; approve aceita vazio
   (omitido); reject/request-changes exigem. Invalida lista+detalhe no sucesso E no ERRO:
   um 409 (conflito — outro staff já decidiu esta submissão) significa que a visão local está
   stale; refetch traz o estado real em vez de deixar o staff olhando uma fila desatualizada. */
/* Dispara o scan manualmente (ADR-0731). Invalida o detalhe para o resultado aparecer quando o
   runner postar o veredito. */
export function useRescanSubmission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => rescanPackSubmission(id),
    onSuccess: () => {
      queryClient
        .invalidateQueries({ queryKey: ['pack-submissions', 'detail'] })
        .catch(() => undefined);
    },
  });
}

export function usePackSubmissionAction() {
  const queryClient = useQueryClient();
  const invalidateQueue = (): void => {
    queryClient
      .invalidateQueries({ queryKey: ['pack-submissions', 'list'] })
      .catch(() => undefined);
    queryClient
      .invalidateQueries({ queryKey: ['pack-submissions', 'detail'] })
      .catch(() => undefined);
  };
  return useMutation({
    mutationFn: ({ id, action, reason }: ActionInput) => {
      if (action === 'start-review') return startReviewPackSubmission(id);
      if (action === 'reopen') return reopenPackSubmission(id);
      if (action === 'reject') return rejectPackSubmission(id, reason);
      if (action === 'request-changes') return requestChangesPackSubmission(id, reason);
      return approvePackSubmission(id, reason.length > 0 ? reason : undefined);
    },
    onSuccess: invalidateQueue,
    /* Erro (incl. 409 de decisão concorrente): re-sincroniza a fila com a verdade do servidor. */
    onError: invalidateQueue,
  });
}

/** Lote homogêneo: o backend devolve sucesso/falha por ID e a fila é re-sincronizada em ambos. */
export function useBulkPackSubmissionAction() {
  const queryClient = useQueryClient();
  const invalidateQueue = (): void => {
    queryClient
      .invalidateQueries({ queryKey: ['pack-submissions', 'list'] })
      .catch(() => undefined);
    queryClient
      .invalidateQueries({ queryKey: ['pack-submissions', 'detail'] })
      .catch(() => undefined);
  };
  return useMutation({
    mutationFn: (input: BulkPackSubmissionActionBody) => bulkPackSubmissionAction(input),
    onSuccess: invalidateQueue,
    onError: invalidateQueue,
  });
}
